import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/Button';
import { Field } from '../components/Field';
import { Reveal } from '../components/Reveal';
import { Accent, SectionHeading } from '../components/SectionHeading';
import { site } from '../content/site';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useHCaptcha } from '@pluperfect/hcaptcha';
import { submitContactForm } from '../api/contact';
import {
  categoryOptions,
  okayToOptions,
  type ContactFormRequest,
  type FieldErrors,
} from '../types/contact';

/** Same expression as the [RegularExpression] on ContactFormModel.PhoneNumber. */
const PHONE_PATTERN = /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;

const EMPTY: ContactFormRequest = {
  name: '',
  email: '',
  phoneNumber: '',
  okayTo: undefined,
  category: '',
  message: '',
  numberOfChildren: undefined,
  agesOfChildren: '',
  captchaToken: '',
};

/**
 * Mirrors the DataAnnotations on the server for immediate feedback. The server still validates
 * everything — this only saves a round trip.
 */
function validate(form: ContactFormRequest): FieldErrors {
  const errors: FieldErrors = {};

  if (!form.name.trim()) errors.name = 'Please tell me your name.';
  if (!form.email.trim()) {
    errors.email = 'Please give me an email address to reply to.';
  } else if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) {
    errors.email = 'That does not look like a valid email address.';
  }
  if (!form.message.trim()) errors.message = 'Please write me a message.';
  if (!form.category) errors.category = 'Please choose what you are looking for.';
  if (form.phoneNumber && !PHONE_PATTERN.test(form.phoneNumber.trim())) {
    errors.phoneNumber = 'The Phone Number field is not in a valid format.';
  }

  return errors;
}

export default function Contact() {
  useDocumentTitle('Contact');

  const [form, setForm] = useState<ContactFormRequest>(EMPTY);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState<string | null>(null);
  const executeCaptcha = useHCaptcha(import.meta.env.VITE_HCAPTCHA_SITE_KEY as string);
  const errorSummary = useRef<HTMLDivElement>(null);

  const update = <K extends keyof ContactFormRequest>(key: K, value: ContactFormRequest[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: undefined, form: undefined }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (sending) return;

    const found = validate(form);
    if (Object.keys(found).length > 0) {
      setErrors(found);
      return;
    }

    setSending(true);
    setErrors({});

    // Minted here rather than on page load: tokens are single use and short lived.
    const token = await executeCaptcha();

    if (!token) {
      setSending(false);
      setErrors({
        form:
          'We could not run the anti-spam check — it may be blocked by an extension or your network.' +
          ` Please email me directly at ${site.email}.`,
      });
      errorSummary.current?.focus();
      return;
    }

    const result = await submitContactForm({ ...form, captchaToken: token });
    setSending(false);

    if (result.ok) {
      setSent(result.message);
      return;
    }

    setErrors(result.errors);
    errorSummary.current?.focus();
  };

  if (sent) {
    return (
      <div className="bg-gradient-to-b from-mint-100 to-cream">
        <div className="mx-auto max-w-2xl px-6 py-24 text-center sm:px-8 sm:py-32">
          <Reveal>
            <div className="rounded-blob bg-white px-8 py-14 shadow-soft">
              <span aria-hidden="true" className="text-5xl">
                💚
              </span>
              <h1 className="mt-6 text-3xl">Thank you!</h1>
              <p className="mt-4 leading-relaxed">{sent}</p>
              <div className="mt-10">
                <Link
                  to="/"
                  className="font-display font-semibold text-mint-700 underline decoration-mint-300 decoration-2 underline-offset-4 transition-colors hover:text-mint-900"
                >
                  ← Back to the home page
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-b from-mint-100 to-cream">
      <div className="mx-auto max-w-3xl px-6 py-16 sm:px-8 sm:py-20">
        <Reveal>
          <SectionHeading eyebrow="Contact">
            Get <Accent>in touch</Accent> with me.
          </SectionHeading>
        </Reveal>

        <Reveal delay={80}>
          <div className="mx-auto mt-8 max-w-xl space-y-4 text-center leading-relaxed">
            <p>
              You might have some questions, and I understand that. I would love to answer your
              questions and respond to your comments!
            </p>
            <p>
              The best way to start a conversation with me is by filling out the form below. I offer
              a wide array of services that I would be happy to go into further detail about.{' '}
              <em>Let&apos;s start a dialog!</em>
            </p>
            <p className="text-sm text-muted">
              Oh, and don&apos;t worry. I don&apos;t store your personal data, and I won&apos;t sell
              it to anyone.
            </p>
          </div>
        </Reveal>

        <Reveal delay={140}>
          <form
            noValidate
            onSubmit={handleSubmit}
            className="mt-12 rounded-blob bg-white p-7 shadow-soft sm:p-10"
          >
            <div
              ref={errorSummary}
              tabIndex={-1}
              aria-live="polite"
              className="focus:outline-none"
            >
              {errors.form && (
                <p className="mb-6 rounded-2xl bg-blush-50 px-5 py-4 text-sm font-semibold text-blush-700 ring-1 ring-blush-200">
                  {errors.form}
                </p>
              )}
            </div>

            <p className="mb-6 text-xs text-muted">
              <span className="text-blush-600">*</span> denotes a required field
            </p>

            <div className="grid gap-5 sm:grid-cols-2">
              <Field id="name" label="Name" required error={errors.name}>
                {(props) => (
                  <input
                    {...props}
                    type="text"
                    autoComplete="name"
                    value={form.name}
                    onChange={(event) => update('name', event.target.value)}
                  />
                )}
              </Field>

              <Field id="email" label="Email" required error={errors.email}>
                {(props) => (
                  <input
                    {...props}
                    type="email"
                    autoComplete="email"
                    value={form.email}
                    onChange={(event) => update('email', event.target.value)}
                  />
                )}
              </Field>

              <Field
                id="phoneNumber"
                label="Phone number"
                error={errors.phoneNumber}
                hint="Providing a phone number tells me you'd rather talk by phone than email."
              >
                {(props) => (
                  <input
                    {...props}
                    type="tel"
                    autoComplete="tel"
                    value={form.phoneNumber ?? ''}
                    onChange={(event) => update('phoneNumber', event.target.value)}
                  />
                )}
              </Field>

              <Field
                id="okayTo"
                label="It is okay to"
                error={errors.okayTo}
                hint="Leave this blank and I'll assume calling or texting is fine."
              >
                {(props) => (
                  <select
                    {...props}
                    value={form.okayTo ?? ''}
                    onChange={(event) =>
                      update('okayTo', (event.target.value || undefined) as ContactFormRequest['okayTo'])
                    }
                  >
                    <option value="">No preference</option>
                    {okayToOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                )}
              </Field>
            </div>

            <div className="mt-5">
              <Field id="category" label="What are you looking for?" required error={errors.category}>
                {(props) => (
                  <select
                    {...props}
                    value={form.category}
                    onChange={(event) =>
                      update('category', event.target.value as ContactFormRequest['category'])
                    }
                  >
                    <option value="">Please choose…</option>
                    {categoryOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                )}
              </Field>
            </div>

            <div className="mt-5">
              <Field id="message" label="Message" required error={errors.message}>
                {(props) => (
                  <textarea
                    {...props}
                    rows={8}
                    className={`${props.className} resize-none`}
                    value={form.message}
                    onChange={(event) => update('message', event.target.value)}
                  />
                )}
              </Field>
            </div>

            <div className="mt-5 grid gap-5 sm:grid-cols-2">
              <Field id="numberOfChildren" label="Number of children" error={errors.numberOfChildren}>
                {(props) => (
                  <input
                    {...props}
                    type="number"
                    min={0}
                    max={20}
                    value={form.numberOfChildren ?? ''}
                    onChange={(event) =>
                      update(
                        'numberOfChildren',
                        event.target.value === '' ? undefined : Number(event.target.value),
                      )
                    }
                  />
                )}
              </Field>

              <Field
                id="agesOfChildren"
                label="Ages of children"
                error={errors.agesOfChildren}
                hint="More than one? A comma separated list is perfect."
              >
                {(props) => (
                  <input
                    {...props}
                    type="text"
                    value={form.agesOfChildren ?? ''}
                    onChange={(event) => update('agesOfChildren', event.target.value)}
                  />
                )}
              </Field>
            </div>

            <p className="mt-8 text-center text-xs leading-relaxed text-muted">
              Your information will not be stored anywhere and will not be sold to anyone. It is
              only used for responding to you. This form is protected by hCaptcha — its{' '}
              <a
                href="https://www.hcaptcha.com/privacy"
                className="underline underline-offset-2 hover:text-mint-700"
                target="_blank"
                rel="noreferrer"
              >
                Privacy Policy
              </a>{' '}
              and{' '}
              <a
                href="https://www.hcaptcha.com/terms"
                className="underline underline-offset-2 hover:text-mint-700"
                target="_blank"
                rel="noreferrer"
              >
                Terms
              </a>{' '}
              apply.
            </p>

            <div className="mt-6 text-center">
              <Button type="submit" variant="blush" disabled={sending}>
                {sending ? 'Sending…' : 'Send my message'}
              </Button>
            </div>
          </form>
        </Reveal>
      </div>
    </div>
  );
}
